// src/services/chat.js

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * P4: Messaging Bridge
 * - Idempotent thread open/create via POST /api/chat/open
 * - Graceful fallback when messaging isn't live
 */

export async function openChat({ other_user_id, token }) {
  const url = `${API}/chat/open`;
  const res = await fetch(url, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify({ other_user_id }),
  });

  // If backend uses feature flags or returns 501/404 when disabled:
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(text || `openChat failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const data = await res.json();
  // Support common shapes: { matchId } or { match_id } or { id }
  const matchId = data.matchId ?? data.match_id ?? data.id;
  if (!matchId) throw new Error("openChat: missing matchId");
  return { matchId };
}

export function isMessagingUnavailableError(err) {
  const status = err?.status;
  return status === 404 || status === 410 || status === 501 || status === 503;
}

/**
 * Open (or create) chat thread idempotently, then navigate.
 * If messaging isn't live, redirect to a graceful fallback screen.
 */
export async function openChatAndNavigate({
  other_user_id,
  token,
  navigate,
  onBusy,
}) {
  try {
    onBusy?.(true);
    const { matchId } = await openChat({ other_user_id, token });
    navigate(`/chat/${matchId}`);
  } catch (err) {
    if (isMessagingUnavailableError(err)) {
      navigate("/messages-coming-soon");
      return;
    }
    throw err;
  } finally {
    onBusy?.(false);
  }
}

export async function requestVideoCallStart(conversationId, token) {
  const res = await fetch(`${API}/chat/video-call/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      conversation_id: conversationId,
    }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to start video call');
  }

  // Expected shape:
  // {
  //   allowed: boolean,
  //   message?: string,
  //   remaining_calls?: number
  // }
  return res.json();
}
